/**
 * Cliente del orchestrator Lambda.
 *
 * Modo dev: si `VITE_API_BASE` no está configurado, se usa un driver local
 * que simula la ejecución de Step Functions para iterar el UI sin AWS.
 */

import { API_CATALOG, Scan, Report, TIER_ORDER, ApiState } from "./types";

const API_BASE = (import.meta.env.VITE_API_BASE as string | undefined)?.replace(/\/$/, "") ?? "";
const API_KEY = (import.meta.env.VITE_API_KEY as string | undefined) ?? "";
const USE_MOCK = !API_BASE;

function authHeaders(extra: Record<string, string> = {}): Record<string, string> {
  const h: Record<string, string> = { ...extra };
  if (API_KEY) h["x-api-key"] = API_KEY;
  return h;
}

export interface StartScanInput {
  target: string;
  apis?: string;
  aiModel?: string;
}

export interface StartScanResponse {
  scanId: string;
  targetId: string;
  executionArn?: string;
  startedAt: string;
}

export async function startScan(input: StartScanInput): Promise<StartScanResponse> {
  if (USE_MOCK) return mockStart(input);
  const r = await fetch(`${API_BASE}/scan`, {
    method: "POST",
    headers: authHeaders({ "Content-Type": "application/json" }),
    body: JSON.stringify(input),
  });
  if (!r.ok) throw new Error(`orchestrator HTTP ${r.status}`);
  return r.json();
}

/* ──────── CAMERAS (Shodan viewer) ──────── */

export interface CameraResult {
  ip: string;
  port: number;
  org: string;
  country: string;
  city: string;
  lat: number | null;
  lon: number | null;
  brand: string;
  product: string;
  hostnames: string[];
  url: string;
  accessible: boolean;
  auth_required: boolean;
  status?: number;
  error?: string;
  bypass_url?: string | null;
}

export interface CameraStats {
  total: number;
  with_country: number;
  http_accessible: number;
  auth_required: number;
  shodan_total: number;
  page: number;
  limit: number;
  total_pages: number;
}

export interface CameraSearchResponse {
  results: CameraResult[];
  stats: CameraStats;
  query: string;
  page: number;
}

export async function searchCameras(input: { query: string; limit?: number; page?: number; engine?: string }): Promise<CameraSearchResponse> {
  if (USE_MOCK) {
    return {
      results: [],
      stats: { total: 0, with_country: 0, http_accessible: 0, auth_required: 0, shodan_total: 0, page: 1, limit: 50, total_pages: 1 },
      query: input.query,
      page: 1,
    };
  }
  const r = await fetch(`${API_BASE}/cameras/search`, {
    method: "POST",
    headers: authHeaders({ "Content-Type": "application/json" }),
    body: JSON.stringify(input),
  });
  if (!r.ok) throw new Error(`cameras HTTP ${r.status}`);
  return r.json();
}

export async function checkBypass(input: { ip: string; port: number; brand: string }): Promise<{ success: boolean; url?: string }> {
  if (USE_MOCK) return { success: false };
  const qs = new URLSearchParams({ ip: input.ip, port: String(input.port), brand: input.brand });
  const r = await fetch(`${API_BASE}/cameras/bypass?${qs}`, {
    headers: authHeaders(),
  });
  if (!r.ok) throw new Error(`bypass HTTP ${r.status}`);
  return r.json();
}

/* ──────── AI EXPOSURE (AI-SEC scanner) ──────── */

export interface AiExposureResult {
  ip: string;
  port: number;
  service: string;
  version: string;
  org: string;
  asn: string;
  country: string;
  countryCode: string;
  city: string;
  lat: number | null;
  lon: number | null;
  isp: string;
  lastSeen: string;
  bannerPreview: string;
  cves: string[];
  riskLevel: "critical" | "high" | "medium";
  category: "llm" | "vectordb" | "ui" | "agents-frameworks" | "unknown";
  probeType?: "ollama" | "openclaw";
  hostnames: string[];
}

export interface AiExposureStats {
  total: number;
  shodan_total: number;
  page: number;
  limit: number;
  total_pages: number;
  by_risk: { critical: number; high: number; medium: number };
  by_category: {
    llm: number;
    vectordb: number;
    ui: number;
    "agents-frameworks"?: number;
  };
}

export interface AiExposureSearchResponse {
  searchId: string;
  service: string;
  query: string;
  page: number;
  results: AiExposureResult[];
  stats: AiExposureStats;
}

export async function searchAiExposure(input: {
  service: string;
  country?: string;
  city?: string;
  limit?: number;
  page?: number;
  engine?: string;
}): Promise<AiExposureSearchResponse> {
  if (USE_MOCK) {
    return {
      searchId: crypto.randomUUID(),
      service: input.service,
      query: `port:11434 product:"Ollama"`,
      page: 1,
      results: [],
      stats: {
        total: 0, shodan_total: 0, page: 1, limit: 25, total_pages: 1,
        by_risk: { critical: 0, high: 0, medium: 0 },
        by_category: { llm: 0, vectordb: 0, ui: 0 },
      },
    };
  }
  const r = await fetch(`${API_BASE}/ai-exposure/search`, {
    method: "POST",
    headers: authHeaders({ "Content-Type": "application/json" }),
    body: JSON.stringify(input),
  });
  if (!r.ok) {
    const errBody = await r.text().catch(() => "");
    let detail = "";
    try { detail = JSON.parse(errBody)?.error || errBody; } catch { detail = errBody; }
    throw new Error(`ai-exposure HTTP ${r.status}${detail ? ": " + detail.slice(0, 200) : ""}`);
  }
  return r.json();
}

export async function lookupAiExposureIp(input: {
  ip: string;
  engine?: string;
}): Promise<AiExposureSearchResponse> {
  if (USE_MOCK) {
    return {
      searchId: crypto.randomUUID(),
      service: "ip_lookup",
      query: input.ip,
      page: 1,
      results: [],
      stats: {
        total: 0, shodan_total: 0, page: 1, limit: 25, total_pages: 1,
        by_risk: { critical: 0, high: 0, medium: 0 },
        by_category: { llm: 0, vectordb: 0, ui: 0 },
      },
    };
  }
  const r = await fetch(`${API_BASE}/ai-exposure/ip-lookup`, {
    method: "POST",
    headers: authHeaders({ "Content-Type": "application/json" }),
    body: JSON.stringify(input),
  });
  if (!r.ok) throw new Error(`ai-exposure ip-lookup HTTP ${r.status}`);
  return r.json();
}

/* ──────── LAB PROBE (AI-SEC active probing) ──────── */

export interface ProbeResult {
  ip: string;
  port: number;
  score: string;
  max_score: number;
  version: Record<string, unknown> | null;
  tags: Record<string, unknown> | null;
  models_count: number;
  model_names: string[];
  probed_at: string;
  // Campos openclaw (opcionales, ausentes en probes ollama)
  probe_type?: "ollama" | "openclaw";
  service_confirmed?: boolean;
  http_status?: number;
  title?: string;
  server_header?: string;
  raw_server_header?: string;
  version_scheme?: "official" | "legacy" | null;
  auth_type?: "none" | "http_401" | "html_form" | "ws" | "unknown";
  auth_detected?: boolean;
  risk_level?: "critical" | "high" | "medium";
  risk_score?: number;
  cve_matches?: string[];
  deep_probe_used?: boolean;
  ws_response?: string;
}

export interface ProbeInput {
  ip: string;
  port: number;
  probeType?: "ollama" | "openclaw";
  deepProbe?: boolean;
}

export async function probeTarget(
  ipOrInput: string | ProbeInput,
  port?: number,
): Promise<ProbeResult> {
  const input: ProbeInput = typeof ipOrInput === "string"
    ? { ip: ipOrInput, port: port ?? 0 }
    : ipOrInput;

  const probeType = input.probeType ?? "ollama";

  if (USE_MOCK) {
    await new Promise(r => setTimeout(r, 2000));
    if (probeType === "openclaw") {
      return {
        ip: input.ip, port: input.port,
        score: "2/2",
        max_score: 2,
        version: { version: "2026.1.15" },
        tags: null,
        models_count: 0,
        model_names: [],
        probed_at: new Date().toISOString(),
        probe_type: "openclaw",
        service_confirmed: true,
        http_status: 200,
        title: "OpenClaw Control",
        server_header: "openclaw/2026.1.15",
        auth_type: input.deepProbe ? "none" : "unknown",
        auth_detected: false,
        risk_level: "critical",
        risk_score: 9,
        cve_matches: ["CVE-2026-25253", "CVE-2026-32001"],
        deep_probe_used: !!input.deepProbe,
        ws_response: input.deepProbe ? "{\"type\":\"hello\"}" : "",
      };
    }
    return {
      ip: input.ip, port: input.port,
      score: "2/2",
      max_score: 2,
      version: { version: "0.1.30" },
      tags: { models: [{ name: "llama3:latest" }, { name: "mistral:latest" }] },
      models_count: 2,
      model_names: ["llama3:latest", "mistral:latest"],
      probed_at: new Date().toISOString(),
      probe_type: "ollama",
    };
  }
  const r = await fetch(`${API_BASE}/lab/probe`, {
    method: "POST",
    headers: authHeaders({ "Content-Type": "application/json" }),
    body: JSON.stringify({
      ip: input.ip,
      port: input.port,
      probe_type: probeType,
      deep_probe: !!input.deepProbe,
    }),
  });
  if (!r.ok) throw new Error(`probe HTTP ${r.status}`);
  return r.json();
}

export async function getAiExposureSearch(searchId: string): Promise<AiExposureSearchResponse> {
  const r = await fetch(`${API_BASE}/ai-exposure/${searchId}`, {
    headers: authHeaders(),
  });
  if (!r.ok) throw new Error(`ai-exposure get HTTP ${r.status}`);
  return r.json();
}

/* ──────── BD-SEC (Database Exposure Scanner via LeakIX) ──────── */

export interface BdSecResult {
  ip: string;
  port: number;
  service: string;
  protocol: string;
  version: string;
  org: string;
  asn: string;
  country: string;
  countryCode: string;
  city: string;
  lat: number | null;
  lon: number | null;
  lastSeen: string;
  leakSeverity: string;
  leakType: string;
  leakStage: string;
  dataSize: string;
  collectionsCount: number | null;
  indicesCount: number | null;
  filesCount: number | null;
  bannerPreview: string;
  riskLevel: "critical" | "high" | "medium" | "low";
  category: "database" | "storage" | "panel" | "config" | "unknown";
  eventSource: string;
}

export interface BdSecStats {
  total: number;
  leakix_returned: number;
  page: number;
  limit: number;
  total_pages: number;
  by_risk: { critical: number; high: number; medium: number; low: number };
  by_category: { database: number; storage: number; panel: number; config: number };
}

export interface BdSecSearchResponse {
  searchId: string;
  service: string;
  query: string;
  page: number;
  results: BdSecResult[];
  stats: BdSecStats;
}

export async function searchBdSec(input: {
  service: string;
  country?: string;
  city?: string;
  limit?: number;
  page?: number;
  engine?: string;
}): Promise<BdSecSearchResponse> {
  if (USE_MOCK) {
    return {
      searchId: crypto.randomUUID(),
      service: input.service,
      query: `protocol:${input.service}`,
      page: 1,
      results: [],
      stats: {
        total: 0, leakix_returned: 0, page: 1, limit: 25, total_pages: 1,
        by_risk: { critical: 0, high: 0, medium: 0, low: 0 },
        by_category: { database: 0, storage: 0, panel: 0, config: 0 },
      },
    };
  }
  const r = await fetch(`${API_BASE}/bd-sec/search`, {
    method: "POST",
    headers: authHeaders({ "Content-Type": "application/json" }),
    body: JSON.stringify(input),
  });
  if (!r.ok) {
    const errBody = await r.text().catch(() => "");
    let detail = "";
    try { detail = JSON.parse(errBody)?.error || errBody; } catch { detail = errBody; }
    throw new Error(`bd-sec HTTP ${r.status}${detail ? ": " + detail.slice(0, 200) : ""}`);
  }
  return r.json();
}

export async function getBdSecSearch(searchId: string): Promise<BdSecSearchResponse> {
  const r = await fetch(`${API_BASE}/bd-sec/${searchId}`, {
    headers: authHeaders(),
  });
  if (!r.ok) throw new Error(`bd-sec get HTTP ${r.status}`);
  return r.json();
}

/* ──────── AGENT-SEC (AI Agent Exposure Scanner) ──────── */

export interface AgentExposureResult {
  ip: string;
  port: number;
  service: string;
  serviceLabel: string;
  version: string;
  org: string;
  asn: string;
  country: string;
  countryCode: string;
  city: string;
  lat: number | null;
  lon: number | null;
  isp: string;
  lastSeen: string;
  bannerPreview: string;
  cves: string[];
  riskLevel: "critical" | "high" | "medium";
  category: "agents-frameworks";
  probeType: "openclaw";
  hostnames: string[];
  dorksMatched: string[];
}

export interface AgentExposurePerDork {
  dork: string;
  hits: number;
  unique_added?: number;
  shodan_total: number;
  error?: string;
}

export interface AgentExposureStats {
  total: number;
  shodan_total: number;
  page: number;
  limit: number;
  total_pages: number;
  queries_executed: number;
  credits_spent: number;
  per_dork: AgentExposurePerDork[];
  by_risk: { critical: number; high: number; medium: number };
  by_service: Record<string, number>;
}

export interface AgentExposureSearchResponse {
  searchId: string;
  service: string;
  query: string;
  page: number;
  results: AgentExposureResult[];
  stats: AgentExposureStats;
  shodan_errors?: string[];
}

export async function searchAgentExposure(input: {
  service: string;
  country?: string;
  city?: string;
  port?: number | "all";
  limit?: number;
  page?: number;
  engine?: string;
}): Promise<AgentExposureSearchResponse> {
  if (USE_MOCK) {
    return {
      searchId: crypto.randomUUID(),
      service: input.service,
      query: 'port:18789 "OpenClaw" | port:443 "OpenClaw Control"',
      page: 1,
      results: [],
      stats: {
        total: 0, shodan_total: 0, page: 1, limit: 25, total_pages: 1,
        queries_executed: 0, credits_spent: 0, per_dork: [],
        by_risk: { critical: 0, high: 0, medium: 0 },
        by_service: { openclaw: 0 },
      },
    };
  }
  const r = await fetch(`${API_BASE}/agent-exposure/search`, {
    method: "POST",
    headers: authHeaders({ "Content-Type": "application/json" }),
    body: JSON.stringify(input),
  });
  if (!r.ok) throw new Error(`agent-exposure HTTP ${r.status}`);
  return r.json();
}

export async function getAgentExposureSearch(searchId: string): Promise<AgentExposureSearchResponse> {
  const r = await fetch(`${API_BASE}/agent-exposure/${searchId}`, {
    headers: authHeaders(),
  });
  if (!r.ok) throw new Error(`agent-exposure get HTTP ${r.status}`);
  return r.json();
}

export type ProgressCb = (scan: Scan) => void;

/**
 * Suscribe al avance de un scan. En modo mock simula el avance localmente.
 * En modo real haría polling a un endpoint /scan/{id} o un WebSocket — TBD.
 */
export function subscribeScan(scan: Scan, cb: ProgressCb): () => void {
  if (USE_MOCK) return mockProgress(scan, cb);
  let stopped = false;
  const tick = async () => {
    if (stopped) return;
    try {
      const r = await fetch(`${API_BASE}/scan/${scan.scanId}`, {
        headers: authHeaders(),
      });
      if (r.ok) cb(await r.json());
    } catch { /* swallow — el statusbar reflejará el degradado */ }
    if (!stopped) setTimeout(tick, 2000);
  };
  tick();
  return () => { stopped = true; };
}

/* ──────── MOCK DRIVER ──────── */

function mockStart(input: StartScanInput): Promise<StartScanResponse> {
  const scanId = crypto.randomUUID();
  const kind = /^(\d{1,3}\.){3}\d{1,3}$/.test(input.target) ? "ip" : "domain";
  return Promise.resolve({
    scanId,
    targetId: `${kind}#${input.target.toLowerCase()}`,
    startedAt: new Date().toISOString(),
  });
}

function mockProgress(scan: Scan, cb: ProgressCb): () => void {
  // Avanza tier por tier, marca cada API en ~250-800ms con resultado aleatorio.
  // No usa Math.random para el outcome dominante (la mayoría termina ok) para
  // que el UI se vea creíble — ~5% error, ~15% skip cuando el tipo no aplica.
  let cancelled = false;
  const apis: ApiState[] = scan.apis.map((a) => ({ ...a }));

  const isIp = scan.targetId.startsWith("ip#");
  const supports: Record<string, "ip" | "domain" | "both"> = {
    greynoise: "ip", abuseipdb: "ip", ipinfo: "ip", bgpview: "ip",
    securitytrails: "domain", crtsh: "domain", builtwith: "domain", hibp: "domain",
  };

  (async () => {
    for (const tier of TIER_ORDER) {
      if (cancelled) return;
      const idxs = apis.map((a, i) => ({ a, i })).filter((x) => x.a.tier === tier);

      // ráfaga paralela del tier
      idxs.forEach((x) => { apis[x.i] = { ...x.a, status: "run" }; });
      cb({ ...scan, apis: [...apis], state: "running" });

      await Promise.all(idxs.map(async (x) => {
        await sleep(250 + Math.floor(Math.random() * 600));
        if (cancelled) return;
        const sup = supports[x.a.name];
        let status: ApiState["status"] = "ok";
        if (sup === "ip" && !isIp) status = "skip";
        else if (sup === "domain" && isIp) status = "skip";
        else status = Math.random() < 0.05 ? "error" : "ok";
        const ms = 200 + Math.floor(Math.random() * 1200);
        apis[x.i] = { ...x.a, status, ms };
        cb({ ...scan, apis: [...apis], state: "running" });
      }));

      await sleep(300); // wait state entre tiers
    }

    cb({
      ...scan,
      apis: [...apis],
      state: "done",
      report: mockReport(scan.targetRaw, isIp),
    });
  })();

  return () => { cancelled = true; };
}

function sleep(ms: number) { return new Promise<void>((r) => setTimeout(r, ms)); }

function mockReport(target: string, isIp: boolean): Report {
  const score = 35 + Math.floor(Math.random() * 50);
  return {
    executive_summary:
      `Análisis OSINT de ${target}. Se correlacionaron señales de ${isIp ? "9" : "12"} fuentes. ` +
      `Riesgo evaluado como ${score >= 70 ? "alto" : score >= 40 ? "medio" : "bajo"}, ` +
      `principalmente por exposición de servicios y huella histórica en certificados.`,
    risk_score: score,
    confirmed_findings: [
      isIp ? "Puerto 22 abierto detectado por Shodan y Censys" : "Subdominio dev.* expuesto en crt.sh y SecurityTrails",
      isIp ? "ASN catalogado como hosting comercial" : "Tecnologías obsoletas reportadas por BuiltWith (jQuery 1.x)",
    ],
    single_source_signals: [
      isIp ? "LeakIX reporta un servicio HTTP con banner verboso" : "HIBP no listó breaches asociados al dominio",
    ],
    exposed_assets: isIp
      ? ["22/tcp ssh", "80/tcp http", "443/tcp https"]
      : ["api.example.com", "dev.example.com", "*.example.com (cert wildcard)"],
    reputation: {
      verdict: score >= 70 ? "suspicious" : "clean",
      sources: { greynoise: "noise", abuseipdb: "0 reports", virustotal: "0/94" },
    },
    recommended_actions: [
      "Reducir banner SSH",
      "Auditar certificados emitidos en crt.sh fuera del proceso oficial",
      "Notificar al propietario vía canal de Responsible Disclosure",
    ],
    validation_plan: [
      "nmap -sV -Pn -p 22,80,443,8080,8443 contra el target",
      "Validación pasiva — ningún script intrusivo (NSE 'safe' only)",
    ],
  };
}

export const _ApiCatalog = API_CATALOG; // reexport for components
